import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Button,
  Input,
  List,
  Typography,
  message,
  Spin,
  Table,
} from "antd";
import { Send, Plus } from "lucide-react";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";
import Cookies from "js-cookie";

const { Text } = Typography;

function Agent() {
  const [conversations, setConversations] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);

  // 🔹 Fetch conversations
  const fetchConversations = async () => {
    try {
      const apiService = new ApiService(
        `${BASE_URL}/apt_ai/conversations`,
        {},
        "GET",
      );

      const res = await apiService.makeCall();
      setConversations(res || []);
    } catch (err) {
      message.error("Failed to load conversations");
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const renderBoldText = (text) => {
  const parts = text.split(/(\*\*\*.*?\*\*\*|\*\*.*?\*\*)/);

  return parts.map((part, i) => {
    if (
      (part.startsWith("***") && part.endsWith("***")) ||
      (part.startsWith("**") && part.endsWith("**"))
    ) {
      return (
        <strong key={i}>
          {part.replace(/^\*\*\*|\*\*\*$|^\*\*|\*\*$/g, "")}
        </strong>
      );
    }
    return part;
  });
};

  const formatMessage = (text) => {
    const parts = text.split(/```/);

    return parts?.map((part, index) => {
      // Code block (every odd index)
      if (index % 2 === 1) {
        return (
          <pre
            key={index}
            style={{
              background: "#1e1e1e",
              color: "#d4d4d4",
              padding: "12px",
              borderRadius: "8px",
              overflowX: "auto",
              fontSize: "13px",
              marginTop: 8,
              marginBottom: 8,
            }}
          >
            <code>{part.replace(/^\w+\n/, "")}</code>
          </pre>
        );
      }

      // Normal text
      return (
        <span
          key={index}
          style={{
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
          }}
        >
          {renderBoldText(part)}
        </span>
      );
    });
  };

  // 🔹 Start new chat
  const startConversation = () => {
    const newSession = crypto.randomUUID();

    setSelectedSession(newSession);
    setMessages([]);

    setConversations((prev) => [
      {
        session_id: newSession,
        last_message: "",
        last_timestamp: new Date(),
        title: "New Chat",
      },
      ...prev,
    ]);
  };

  // 🔹 Select conversation
  const selectConversation = async (sessionId) => {
    setSelectedSession(sessionId);
    setLoading(true);

    try {
      const apiService = new ApiService(
        `${BASE_URL}/apt_ai/messages?session_id=${sessionId}`,
        {},
        "GET",
      );

      const res = await apiService.makeCall();

      const parsedMessages = (res || [])?.map((msg) => {
        try {
          const parsed = JSON.parse(msg.content);

          // If it's a table, store parsed object
          if (parsed && parsed.type === "table") {
            return { ...msg, content: parsed };
          }

          // Optional: If it's text, store parsed text
          if (parsed && parsed.text) {
            return { ...msg, content: parsed.text };
          }
        } catch (e) {
          console.warn("Failed to parse message content:", msg.content, e);
        }

        // fallback: original content (string)
        return msg;
      });

      setMessages(parsedMessages);
    } catch (err) {
      message.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Send message (STREAMING)
  const sendMessage = async () => {
    if (!input.trim()) return;

    let sessionId = selectedSession;

    // Create session if not exists
    if (!sessionId) {
      sessionId = crypto.randomUUID();
      setSelectedSession(sessionId);
    }

    const userMsg = {
      role: "user",
      content: input,
    };

    setMessages((prev) => [...prev, userMsg]);

    const tempInput = input;
    setInput("");

    let aiMessage = {
      role: "assistant",
      content: "",
    };

    setMessages((prev) => [...prev, aiMessage]);
    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/apt_ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("tokenRes")}`,
        },
        body: JSON.stringify({
          message: tempInput,
          session_id: sessionId,
        }),
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let done = false;

      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter(Boolean);

        for (let line of lines) {
          try {
            const parsed = JSON.parse(line);

            // 🔥 HANDLE TABLE RESPONSE
            if (parsed.type === "table") {
              aiMessage.content = parsed; // store full object

              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...aiMessage };
                return updated;
              });

              continue;
            }

            // 🔥 HANDLE NORMAL TEXT STREAM
            if (parsed.text) {
              aiMessage.content += parsed.text;

              setMessages((prev) => {
                const updated = [...prev];
                updated[updated.length - 1] = { ...aiMessage };
                return updated;
              });
            }
          } catch (err) {
            // ignore bad chunks
          }
        }
      }

      // 🔥 Refresh sidebar after response
      fetchConversations();
    } catch (err) {
      console.error(err);
      message.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Auto scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const renderTable = (data) => {
    const columns = data?.columns?.map((col) => ({
      title: col,
      dataIndex: col,
      key: col,
      ellipsis: true,
    }));

    const dataSource = data?.rows?.map((row, index) => {
      const obj = { key: index };
      data.columns.forEach((col, colIndex) => {
        obj[col] = row[colIndex] !== null ? row[colIndex].toString() : "-";
      });
      return obj;
    });

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={{ pageSize: 5, size: "small" }}
        size="small"
        scroll={{ x: "max-content" }} // horizontal scroll for wide tables
        bordered
        style={{
          width: "1000px",
          height: "auto",
          left:280
        }}
      />
    );
  };

  return (
    <div style={{ height: "100vh", display: "flex", background: "#f5f7fb" }}>
      {/* Sidebar */}
      <div
        style={{
          width: 280,
          marginTop: 65,
          borderRight: "1px solid #f0f0f0",
          display: "flex",
          flexDirection: "column",
          background: "#fafafa",
          height: "calc(100vh - 65px)",
        }}
      >
        <Button
          type="primary"
          icon={<Plus size={16} />}
          style={{ margin: 12, borderRadius: 8, height: 40 }}
          onClick={startConversation}
        >
          New Chat
        </Button>

        <div style={{ flex: 1, overflowY: "auto" }}>
          <List
            dataSource={conversations}
            renderItem={(conv) => (
              <List.Item
                onClick={() => selectConversation(conv.session_id)}
                style={{
                  cursor: "pointer",
                  padding: "12px 16px",
                  background:
                    selectedSession === conv.session_id
                      ? "#e6f4ff"
                      : "transparent",
                }}
              >
                <div>
                  <Text strong>{conv.title || "New Chat"}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {conv.last_timestamp
                      ? new Date(conv.last_timestamp).toLocaleString()
                      : ""}
                  </Text>
                </div>
              </List.Item>
            )}
          />
        </div>
      </div>

      {/* Header and Chat Area Combined */}
      <div style={{ flex: 1, marginTop: 65, display: "flex", flexDirection: "column", position: "relative" }}>
        {/* Header */}
        <div style={{ textAlign: "center", padding: "20px", background: "#fff", borderBottom: "1px solid #eee" }}>
          <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "bold", color: "#1677ff" }}>Apt AI</h1>
        </div>

        {/* Messages */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            background: "#f5f7fb",
            paddingBottom: "100px", // Space for fixed input
          }}
        >
          {/* Messages */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "20px",
              background: "#f5f7fb",
              paddingBottom: "100px", // Space for fixed input
            }}
          >
            {messages?.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  marginTop: "20%",
                  color: "#999",
                }}
              >
                <h2>✨ Ask me anything</h2>
                <p>Start a conversation</p>
              </div>
            ) : (
              messages?.map((msg, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{
                      padding: "6px 8px",
                      borderRadius: 16,
                      maxWidth:
                        typeof msg.content === "object" &&
                        msg.content?.type === "table"
                          ? "100%" // 💡 table bubbles slightly wider than normal messages
                          : "100%",
                      background: msg.role === "user" ? "#1677ff" : "#fff",
                      color: msg.role === "user" ? "#fff" : "#000",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                      fontSize: 14,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {typeof msg.content === "object" &&
                    msg?.content?.type === "table"
                      ? renderTable(msg.content)
                      : formatMessage(msg.content)}
                  </div>
                </div>
              ))
            )}

            {loading && (
              <div style={{ textAlign: "left", marginTop: 10 }}>
                <Spin size="small" />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input - Fixed at bottom */}
          <div
            style={{
              position: "fixed",
              bottom: 0,
              left: 280, // Sidebar width
              right: 0,
              display: "flex",
              padding: 16,
              borderTop: "1px solid #eee",
              background: "#fff",
              zIndex: 10,
            }}
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask anything..."
              onPressEnter={sendMessage}
              style={{
                borderRadius: 20,
                height: 42,
                paddingLeft: 16,
              }}
            />

            <Button
              type="primary"
              icon={<Send size={18} />}
              onClick={sendMessage}
              style={{
                marginLeft: 10,
                borderRadius: "50%",
                width: 42,
                height: 42,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Agent;
